import React, { createContext, useContext, useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate, NavigateOptions, useLocation } from 'react-router-dom';

import navigationService from '../services/navigationService';

export interface NavigationContextType {
  navigateTo: (path: string, options?: NavigateOptions) => void;
  navigateToLogin: () => void;
  navigateToSignup: () => void;
  navigateToChat: () => void;
  navigateToProfile: () => void;
  navigateToDocument: (documentId: string) => void;
}

const NavigationContext = createContext<NavigationContextType | undefined>(undefined);

export const NavigationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const navigateRef = useRef(navigate);

  // Update ref when navigate changes
  navigateRef.current = navigate;

  const navigateTo = useCallback((path: string, options?: NavigateOptions) => {
    navigateRef.current(path, options);
  }, []);

  const navigateToLogin = useCallback(() => {
    navigateRef.current('/login');
  }, []);

  const navigateToSignup = useCallback(() => {
    navigateRef.current('/signup');
  }, []);

  const navigateToChat = useCallback(() => {
    navigateRef.current('/chat');
  }, []);

  const navigateToProfile = useCallback(() => {
    navigateRef.current('/profile');
  }, []);

  const navigateToDocument = useCallback((documentId: string) => {
    navigateRef.current(`/documents/${documentId}`);
  }, []);

  const value = useMemo(
    () => ({
      navigateTo,
      navigateToLogin,
      navigateToSignup,
      navigateToChat,
      navigateToProfile,
      navigateToDocument,
    }),
    [
      navigateTo,
      navigateToLogin,
      navigateToSignup,
      navigateToChat,
      navigateToProfile,
      navigateToDocument,
    ],
  );

  // Initialize the navigationService with the current navigation context
  useEffect(() => {
    navigationService.init(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Check session expiration on route changes
  useEffect(() => {
    // Skip for login, signup, forgot-password, and OAuth callback routes
    if (
      location.pathname !== '/login' &&
      location.pathname !== '/signup' &&
      location.pathname !== '/forgot-password' &&
      location.pathname !== '/' &&
      !location.pathname.startsWith('/auth/')
    ) {
      navigationService.checkSessionExpiration();
    }
  }, [location.pathname]);

  return <NavigationContext.Provider value={value}>{children}</NavigationContext.Provider>;
};

export const useNavigation = (): NavigationContextType => {
  const context = useContext(NavigationContext);

  if (context === undefined) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }

  return context;
};

/** Non-throwing variant for callers that may remount during Vite HMR without a matching provider instance. */
export const useOptionalNavigation = (): NavigationContextType | undefined => {
  return useContext(NavigationContext);
};
